import React from "react";

import loadable from "@loadable/component";

/**
 * A wrapper to help with breaking apart our React frontend apps in logical
 * segments in ways that our bundlers can't automatically infer. This helps
 * a ton to ensure a smooth UX with really good cachables for users.
 *
 * @example
 * ```ts
 * import { asyncComponent } from "@kazoohr/confetti/asyncComponent";
 *
 * const ActionList = asyncComponent({
 *   module: () => import("./ActionList"),
 *   component: (module) => module["default"],
 * });
 * ```
 */
export function asyncComponent<Module extends Promise<any>, Props>({
  module,
  component,
  fallback,
}: {
  module: () => Module;
  component: (module: Awaited<Module>) => React.ComponentType<Props>;
  /**
   * Displayed while the component is loading
   */
  fallback?: JSX.Element;
}): React.ComponentType<Props> {
  // Ignored because this is a library component, and we want to make sure that
  // no matter what loadable library we use in the future, we don't want to leak
  // any of the underlying implementation into the public API.
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return loadable(module, {
    resolveComponent: component,
    fallback,
  }) as React.ComponentType<Props>;
}
